/**
 * Dubletten-Erkennung für Forderungsakten.
 *
 * Bis die View `kunden_mit_mehreren_akten` (#267 Backend) live ist, berechnen
 * wir die Dubletten-Warnung im Frontend: dieselbe `kunde_id` mit mindestens
 * einer weiteren noch nicht erledigten Akte, oder überlappende Mietzeiträume
 * beim selben Kunden.
 */

import type { Forderungsakte } from '@/hooks/useForderungen';

const STATUS_ERLEDIGT = new Set(['erledigt', 'abgerechnet']);

function istOffen(f: Pick<Forderungsakte, 'status'>): boolean {
  return !STATUS_ERLEDIGT.has(f.status);
}

function zeitraumUeberlappt(
  a: Pick<Forderungsakte, 'mietbeginn' | 'mietende'>,
  b: Pick<Forderungsakte, 'mietbeginn' | 'mietende'>,
): boolean {
  if (!a.mietbeginn || !a.mietende || !b.mietbeginn || !b.mietende) return false;
  const aStart = new Date(a.mietbeginn).getTime();
  const aEnd = new Date(a.mietende).getTime();
  const bStart = new Date(b.mietbeginn).getTime();
  const bEnd = new Date(b.mietende).getTime();
  if (isNaN(aStart) || isNaN(aEnd) || isNaN(bStart) || isNaN(bEnd)) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

export interface DublettenTreffer {
  id: string;
  akten_nr: string | null;
  status: Forderungsakte['status'];
  mietbeginn: string | null;
  mietende: string | null;
  grund: 'weitere_offene_akte' | 'ueberlappender_zeitraum';
}

/**
 * Findet andere Forderungsakten desselben Kunden, die eine Dubletten-Warnung
 * rechtfertigen. Gibt eine leere Liste zurück, wenn der Kunde eindeutig ist.
 */
export function findeDubletten(
  aktuell: Forderungsakte,
  alle: Forderungsakte[],
): DublettenTreffer[] {
  if (!aktuell.kunde_id) return [];

  const treffer: DublettenTreffer[] = [];
  for (const andere of alle) {
    if (andere.id === aktuell.id) continue;
    if (andere.kunde_id !== aktuell.kunde_id) continue;

    if (zeitraumUeberlappt(aktuell, andere)) {
      treffer.push({
        id: andere.id,
        akten_nr: andere.akten_nr,
        status: andere.status,
        mietbeginn: andere.mietbeginn,
        mietende: andere.mietende,
        grund: 'ueberlappender_zeitraum',
      });
      continue;
    }

    if (istOffen(aktuell) && istOffen(andere)) {
      treffer.push({
        id: andere.id,
        akten_nr: andere.akten_nr,
        status: andere.status,
        mietbeginn: andere.mietbeginn,
        mietende: andere.mietende,
        grund: 'weitere_offene_akte',
      });
    }
  }
  return treffer;
}

export function hatDublettenWarnung(
  aktuell: Forderungsakte,
  alle: Forderungsakte[],
): boolean {
  return findeDubletten(aktuell, alle).length > 0;
}
